// components/monitoring/RealtimeBandwidthChart.jsx  (React web)
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// ─── Tune these ────────────────────────────────────────────────────────────────
const VISIBLE_POINTS  = 100;    // points visible at once in the window
const MIN_SPACING     = 3;     // px — minimum gap between points
const MAX_DATA_POINTS = 200;   // 10 min @ 3 s intervals
// ──────────────────────────────────────────────────────────────────────────────

const CHART_HEIGHT   = 300;
const Y_AXIS_WIDTH   = 70;
const X_AXIS_HEIGHT  = 60;

// Color palette per WAN
const WAN_COLORS = [
  { download: '#2C7DA0', upload: '#52B788' },
  { download: '#E91E63', upload: '#F48FB1' },
  { download: '#FF9800', upload: '#FFCC80' },
  { download: '#9C27B0', upload: '#CE93D8' },
  { download: '#009688', upload: '#80CBC4' },
  { download: '#F44336', upload: '#EF9A9A' },
];

const MODE_TOTAL  = 'total';
const MODE_SPLIT  = 'split';
const MODE_SINGLE = 'single';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatBandwidth = (kbps) => {
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
  return `${Math.round(kbps)} kbps`;
};

const parseUTCTimeString = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'number') return new Date(timestamp * 1000);
  if (timestamp.includes('Z') || timestamp.includes('+')) return new Date(timestamp);
  try { return new Date(timestamp.replace(' ', 'T') + 'Z'); } catch { return null; }
};

const formatTimestamp = (ts) => {
  const date = parseUTCTimeString(ts);
  if (!date || isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(date);
};

// ─── Main component ───────────────────────────────────────────────────────────
const RealtimeBandwidthChart = ({ serialNumber }) => {
  // ── Mode & WAN selection ───────────────────────────────────────────────────
  const [mode,          setMode]          = useState(MODE_TOTAL);
  const [selectedWan,   setSelectedWan]   = useState(null); // null = aggregate
  const [wanList,       setWanList]       = useState([]);   // [{ id, name }]
  const [wanOptions,    setWanOptions]    = useState([]);
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [hasLoadedWans, setHasLoadedWans] = useState(false);
  const [hiddenWans,    setHiddenWans]    = useState(new Set());

  // ── Data ───────────────────────────────────────────────────────────────────
  const [graphData,  setGraphData]  = useState([]);           // TOTAL / SINGLE mode
  const [splitData,  setSplitData]  = useState({});           // SPLIT mode: { [wanId]: [...] }

  // ── UI ─────────────────────────────────────────────────────────────────────
  const [isLoading,        setIsLoading]        = useState(false);
  const [lastUpdate,       setLastUpdate]        = useState(null);
  const [tooltipData,      setTooltipData]       = useState(null);
  const [tooltipPosition,  setTooltipPosition]   = useState({ x: 0, y: 0 });
  const [showTooltip,      setShowTooltip]       = useState(false);
  const [stats, setStats] = useState({
    current: { download: 0, upload: 0, total: 0 },
    average: { download: 0, upload: 0, total: 0 },
    peak:    { download: 0, upload: 0, total: 0 },
  });

  const intervalRef      = useRef(null);
  const isMounted        = useRef(true);
  const scrollContainerRef = useRef(null);
  const svgRef           = useRef(null);

  // ── Dynamic point spacing ──────────────────────────────────────────────────
  const containerWidth = typeof window !== 'undefined' ? window.innerWidth - 100 : 1200;
  const pointSpacing   = Math.max(MIN_SPACING, Math.floor((containerWidth - Y_AXIS_WIDTH - 40) / VISIBLE_POINTS));
  const isMobile       = typeof window !== 'undefined' && window.innerWidth < 768;

  const currentLength = mode === MODE_SPLIT
    ? Math.max(...Object.values(splitData).map(a => a.length), 0)
    : graphData.length;

  const chartWidth = Math.max(
    containerWidth,
    Y_AXIS_WIDTH + 40 + currentLength * pointSpacing
  );

  // Auto-scroll to end on new data
  const scrollToEnd = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, []);

  useEffect(() => {
    if (currentLength > 0) setTimeout(scrollToEnd, 50);
  }, [currentLength, scrollToEnd]);

  // ── Stats (TOTAL / SINGLE) ─────────────────────────────────────────────────
  const calculateStats = (dataPoints) => {
    if (!dataPoints?.length) return {
      current: { download: 0, upload: 0, total: 0 },
      average: { download: 0, upload: 0, total: 0 },
      peak:    { download: 0, upload: 0, total: 0 },
    };
    const current = dataPoints[dataPoints.length - 1];
    let sumD = 0, sumU = 0, sumT = 0, pkD = 0, pkU = 0, pkT = 0;
    dataPoints.forEach(p => {
      sumD += p.download; sumU += p.upload; sumT += p.total;
      if (p.download > pkD) pkD = p.download;
      if (p.upload   > pkU) pkU = p.upload;
      if (p.total    > pkT) pkT = p.total;
    });
    const n = dataPoints.length;
    return {
      current: { download: current.download, upload: current.upload, total: current.total },
      average: { download: sumD / n, upload: sumU / n, total: sumT / n },
      peak:    { download: pkD, upload: pkU, total: pkT },
    };
  };

  useEffect(() => {
    if (mode !== MODE_SPLIT && graphData.length > 0) setStats(calculateStats(graphData));
  }, [graphData, mode]);

  // ── Fetch — TOTAL / SINGLE ─────────────────────────────────────────────────
  const fetchTotal = useCallback(async () => {
    if (!isMounted.current || !serialNumber) return;
    setIsLoading(true);
    try {
      let url = `/api/peplink/bandwidth?serial_number=${serialNumber}`;
      if (selectedWan !== null) url += `&wan_id=${selectedWan}`;
      const res  = await fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (data?.data) {
        if (!hasLoadedWans && data.data.wans?.length > 0) {
          const list = data.data.wans.map(w => ({ id: w.id, name: w.name }));
          setWanList(list);
          setWanOptions([
            { label: 'All WANs (Total)',      mode: MODE_TOTAL,  wanId: null },
            { label: 'All WANs (Individual)', mode: MODE_SPLIT,  wanId: null },
            ...list.map(w => ({ label: w.name, mode: MODE_SINGLE, wanId: w.id })),
          ]);
          setHasLoadedWans(true);
        }
        const pt = {
          timestamp: data.data.timestamp || new Date().toISOString(),
          download:  data.data.rx || 0,
          upload:    data.data.tx || 0,
          total:    (data.data.rx || 0) + (data.data.tx || 0),
        };
        setGraphData(prev => {
          const next = [...prev, pt];
          return next.length > MAX_DATA_POINTS ? next.slice(-MAX_DATA_POINTS) : next;
        });
        setLastUpdate(new Date());
      }
    } catch (err) {
      console.error('Error fetching bandwidth:', err);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [serialNumber, selectedWan, hasLoadedWans]);

  // ── Fetch — SPLIT (all WANs in parallel) ──────────────────────────────────
  const fetchSplit = useCallback(async () => {
    if (!isMounted.current || !serialNumber || wanList.length === 0) return;
    setIsLoading(true);
    try {
      const results = await Promise.all(
        wanList.map(wan =>
          fetch(`/api/peplink/bandwidth?serial_number=${serialNumber}&wan_id=${wan.id}`, {
            credentials: 'include', headers: { 'Content-Type': 'application/json' },
          })
            .then(r => r.json())
            .then(d => ({ wanId: wan.id, data: d?.data }))
            .catch(() => ({ wanId: wan.id, data: null }))
        )
      );
      const ts = new Date().toISOString();
      setSplitData(prev => {
        const next = { ...prev };
        results.forEach(({ wanId, data }) => {
          if (!data) return;
          const pt = {
            timestamp: data.timestamp || ts,
            download: data.rx || 0,
            upload:   data.tx || 0,
            total:   (data.rx || 0) + (data.tx || 0),
          };
          const arr = next[wanId] ? [...next[wanId], pt] : [pt];
          next[wanId] = arr.length > MAX_DATA_POINTS ? arr.slice(-MAX_DATA_POINTS) : arr;
        });
        return next;
      });
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching split bandwidth:', err);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [serialNumber, wanList]);

  // ── Polling ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!serialNumber) return;
    isMounted.current = true;
    setGraphData([]);
    setSplitData({});
    setHiddenWans(new Set());

    const fetch = mode === MODE_SPLIT ? fetchSplit : fetchTotal;
    fetch();
    intervalRef.current = setInterval(fetch, 3000);
    return () => {
      isMounted.current = false;
      clearInterval(intervalRef.current);
    };
  }, [mode, fetchTotal, fetchSplit, serialNumber]);

  // ── Y-axis ─────────────────────────────────────────────────────────────────
  const getMaxValue = useMemo(() => {
    if (mode === MODE_SPLIT) {
      const visibleWans = wanList.filter(w => !hiddenWans.has(w.id));
      let max = 1000;
      visibleWans.forEach(wan => {
        const series = splitData[wan.id] ?? [];
        series.forEach(p => {
          if (p.download > max) max = p.download;
          if (p.upload   > max) max = p.upload;
        });
      });
      return Math.ceil(max * 1.1);
    }
    if (graphData.length === 0) return 1000;
    const max = Math.max(
      Math.max(...graphData.map(d => d.download)),
      Math.max(...graphData.map(d => d.upload))
    );
    return Math.ceil(max * 1.1);
  }, [graphData, splitData, mode, hiddenWans, wanList]);

  const yAxisTicks = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => (getMaxValue / 5) * i);
  }, [getMaxValue]);

  const getY = (value) =>
    CHART_HEIGHT - X_AXIS_HEIGHT - ((value / getMaxValue) * (CHART_HEIGHT - X_AXIS_HEIGHT - 40));

  const getX = (index) => Y_AXIS_WIDTH + 20 + index * pointSpacing;

  // ── Smooth cubic bezier path from array of {x,y} points ──────────────────
  const makePath = (pts) => {
    if (pts.length < 2) return '';
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1];
      const curr = pts[i];
      const cpx  = (prev.x + curr.x) / 2;
      // Cubic bezier: control points share the midpoint x, keeping y flat
      d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
    }
    return d;
  };

  // ── Build lines for SVG ────────────────────────────────────────────────────
  const lines = useMemo(() => {
    if (mode === MODE_TOTAL || mode === MODE_SINGLE) {
      if (graphData.length < 2) return [];
      return [
        {
          key: 'download',
          color: '#D7D7CC',
          strokeWidth: 2,
          path: makePath(graphData.map((p, i) => ({ x: getX(i), y: getY(p.download) }))),
        },
        {
          key: 'upload',
          color: '#88F4EB',
          strokeWidth: 2,
          path: makePath(graphData.map((p, i) => ({ x: getX(i), y: getY(p.upload) }))),
        },
      ];
    }

    // SPLIT mode
    const result = [];
    const visibleWans = wanList.filter(w => !hiddenWans.has(w.id));
    const minLen = Math.min(...visibleWans.map(w => (splitData[w.id] ?? []).length));
    if (minLen < 2) return [];

    visibleWans.forEach((wan, idx) => {
      const series = (splitData[wan.id] ?? []).slice(-minLen);
      const colors = WAN_COLORS[idx % WAN_COLORS.length];

      result.push({
        key: `${wan.id}-dl`,
        color: colors.download,
        strokeWidth: 2,
        path: makePath(series.map((p, i) => ({ x: getX(i), y: getY(p.download) }))),
      });
      result.push({
        key: `${wan.id}-ul`,
        color: colors.upload,
        strokeWidth: 1.5,
        strokeDasharray: '5,3',
        path: makePath(series.map((p, i) => ({ x: getX(i), y: getY(p.upload) }))),
      });
    });
    return result;
  }, [graphData, splitData, mode, hiddenWans, wanList, getMaxValue, pointSpacing]);

  // ── Tooltip on mouse move ──────────────────────────────────────────────────
  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const rect   = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;

    if (mode === MODE_SPLIT) {
      // Use first visible WAN's series for index lookup
      const firstWan = wanList.find(w => !hiddenWans.has(w.id));
      if (!firstWan) return;
      const series = splitData[firstWan.id] ?? [];
      const minLen = Math.min(...wanList.filter(w => !hiddenWans.has(w.id)).map(w => (splitData[w.id] ?? []).length));
      const sliced = series.slice(-minLen);

      let closest = -1, minDist = Infinity;
      sliced.forEach((_, i) => {
        const dist = Math.abs(mouseX - getX(i));
        if (dist < minDist && dist < 50) { minDist = dist; closest = i; }
      });

      if (closest === -1) { setShowTooltip(false); return; }

      // Build tooltip rows for all visible WANs
      const rows = [];
      wanList.filter(w => !hiddenWans.has(w.id)).forEach((wan, idx) => {
        const s = (splitData[wan.id] ?? []).slice(-minLen);
        const p = s[closest];
        if (!p) return;
        const colors = WAN_COLORS[idx % WAN_COLORS.length];
        rows.push({ label: `${wan.name} ↓`, value: p.download, color: colors.download });
        rows.push({ label: `${wan.name} ↑`, value: p.upload,   color: colors.upload   });
      });

      const refSeries = (splitData[firstWan.id] ?? []).slice(-minLen);
      setTooltipData({
        time: formatTimestamp(refSeries[closest]?.timestamp),
        fullTime: new Date(refSeries[closest]?.timestamp).toLocaleString(),
        rows,
        isSplit: true,
      });
      setTooltipPosition({ x: getX(closest) + 10, y: 50 });
      setShowTooltip(true);
      return;
    }

    // TOTAL / SINGLE
    let closest = -1, minDist = Infinity;
    graphData.forEach((_, i) => {
      const dist = Math.abs(mouseX - getX(i));
      if (dist < minDist && dist < 50) { minDist = dist; closest = i; }
    });

    if (closest === -1) { setShowTooltip(false); return; }
    const p = graphData[closest];
    setTooltipData({
      time:     formatTimestamp(p.timestamp),
      fullTime: new Date(p.timestamp).toLocaleString(),
      download: p.download,
      upload:   p.upload,
      total:    p.total,
      isSplit:  false,
    });
    setTooltipPosition({ x: getX(closest) + 10, y: 50 });
    setShowTooltip(true);
  };

  // ── WAN select handler ─────────────────────────────────────────────────────
  const handleWanOptionSelect = (option) => {
    setMode(option.mode);
    setSelectedWan(option.wanId);
    setShowDropdown(false);
    setGraphData([]);
    setSplitData({});
    setHiddenWans(new Set());
  };

  const toggleWanVisibility = (wanId) => {
    setHiddenWans(prev => {
      const next = new Set(prev);
      next.has(wanId) ? next.delete(wanId) : next.add(wanId);
      return next;
    });
  };

  const getSelectedLabel = () => {
    if (mode === MODE_SPLIT)  return 'All WANs (Individual)';
    if (selectedWan === null) return 'All WANs (Total)';
    return wanList.find(w => w.id === selectedWan)?.name ?? 'Select WAN';
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const hasData = mode === MODE_SPLIT
    ? Object.values(splitData).some(a => a.length > 0)
    : graphData.length > 0;

  // X-axis labels — which indices get a label
  // - For the first 10 points: just show the oldest (index 0) and the latest (last index).
  // - Beyond 10 points: unchanged — every Nth point, interval grows as data grows.
  const xLabelIndices = useMemo(() => {
    if (currentLength === 0) return new Set();

    if (currentLength <= 10) {
      return currentLength === 1
        ? new Set([0])
        : new Set([0, currentLength - 1]);
    }

    // 11+ points: fixed interval of 5 on mobile (less crowded), original
    // dynamic "every Nth point" behavior on desktop.
    const interval = isMobile ? 5 : (Math.ceil(currentLength / 10) || 1);
    const indices = new Set();
    for (let i = 0; i < currentLength; i += interval) indices.add(i);
    return indices;
  }, [currentLength, isMobile]);

  if (!serialNumber) {
    return <div style={s.container}><p style={s.errorText}>No device selected</p></div>;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={s.container}>

      {/* WAN selector */}
      <div style={s.selectorRow}>
        <label style={s.selectorLabel}>SELECT WAN</label>
        <div style={s.selectWrapper}>
          <button onClick={() => setShowDropdown(d => !d)} style={s.selectButton}>
            <span>{getSelectedLabel()}</span>
            <span style={s.dropdownArrow}>▼</span>
          </button>
          {showDropdown && (
            <div style={s.dropdownMenu}>
              {wanOptions.map((opt, i) => {
                const isSelected =
                  opt.mode === MODE_SPLIT  ? mode === MODE_SPLIT :
                  opt.mode === MODE_TOTAL  ? mode === MODE_TOTAL && selectedWan === null :
                  mode === MODE_SINGLE && selectedWan === opt.wanId;
                return (
                  <button
                    key={i}
                    onClick={() => handleWanOptionSelect(opt)}
                    style={{ ...s.dropdownItem, ...(isSelected ? s.dropdownItemSelected : {}) }}
                  >
                    {opt.label}
                    {isSelected && <span style={s.checkmark}>✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Last update */}
      {lastUpdate && (
        <p style={s.updateTime}>
          LAST UPDATE {formatTimestamp(lastUpdate)} · {currentLength} POINTS · MAX 10 MIN
        </p>
      )}

      {/* Chart */}
      <div ref={scrollContainerRef} style={s.scrollContainer}>
        <div style={{ width: chartWidth, position: 'relative' }}>
          <svg
            ref={svgRef}
            width={chartWidth}
            height={CHART_HEIGHT}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setShowTooltip(false)}
            style={{ backgroundColor: 'var(--color-card)', display: 'block' }}
          >
            {/* Grid lines */}
            {yAxisTicks.map((tick, i) => (
              <line key={`g${i}`}
                x1={Y_AXIS_WIDTH + 10} y1={getY(tick)}
                x2={chartWidth - 20}   y2={getY(tick)}
                stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="5,5"
              />
            ))}

            {/* Axes */}
            <line x1={Y_AXIS_WIDTH + 10} y1={20}
                  x2={Y_AXIS_WIDTH + 10} y2={CHART_HEIGHT - X_AXIS_HEIGHT}
                  stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
            <line x1={Y_AXIS_WIDTH + 10} y1={CHART_HEIGHT - X_AXIS_HEIGHT}
                  x2={chartWidth - 20}  y2={CHART_HEIGHT - X_AXIS_HEIGHT}
                  stroke="rgba(255,255,255,0.25)" strokeWidth="2" />

            {/* Lines — smooth cubic bezier, no dots */}
            {lines.map(line => (
              <path
                key={line.key}
                d={line.path}
                fill="none"
                stroke={line.color}
                strokeWidth={line.strokeWidth ?? 2}
                strokeDasharray={line.strokeDasharray ?? undefined}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}

            {/* X-axis labels — thinned out */}
            {(mode === MODE_SPLIT
              ? (splitData[wanList[0]?.id] ?? []).slice(-(Math.min(...wanList.map(w => (splitData[w.id] ?? []).length)) || 0))
              : graphData
            ).map((pt, i) => {
              if (!xLabelIndices.has(i)) return null;
              const x = getX(i);
              return (
                <text key={`xl${i}`} x={x} y={CHART_HEIGHT - X_AXIS_HEIGHT + 20}
                  fontSize="9" fill="rgba(255,255,255,0.52)" textAnchor="middle"
                  transform={`rotate(-35,${x},${CHART_HEIGHT - X_AXIS_HEIGHT + 20})`}
                >
                  {formatTimestamp(pt.timestamp)}
                </text>
              );
            })}

            {/* Y-axis labels */}
            {yAxisTicks.map((tick, i) => (
              <text key={`yl${i}`} x={Y_AXIS_WIDTH} y={getY(tick) + 4}
                fontSize="10" fill="rgba(255,255,255,0.52)" textAnchor="end"
              >
                {formatBandwidth(tick)}
              </text>
            ))}

            {/* Y-axis title */}
            <text x="13" y={CHART_HEIGHT / 2} fontSize="11" fill="rgba(255,255,255,0.52)"
              textAnchor="middle" transform={`rotate(-90,13,${CHART_HEIGHT / 2})`}
            >
              Bandwidth
            </text>
          </svg>

          {/* Tooltip — unchanged from original */}
          {showTooltip && tooltipData && (
            <div style={{
              ...s.tooltip,
              position: 'absolute',
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              pointerEvents: 'none',
              zIndex: 1000,
            }}>
              <p style={s.tooltipTime}>{tooltipData.time}</p>
              {tooltipData.isSplit ? (
                tooltipData.rows.map((row, i) => (
                  <p key={i} style={s.tooltipText}>
                    <span style={{ color: row.color, marginRight: 6 }}>●</span>
                    {row.label}:&nbsp;
                    <strong>{formatBandwidth(row.value)}</strong>
                  </p>
                ))
              ) : (
                <>
                  <p style={s.tooltipText}>Download: <strong>{formatBandwidth(tooltipData.download)}</strong></p>
                  <p style={s.tooltipText}>Upload: <strong>{formatBandwidth(tooltipData.upload)}</strong></p>
                  <p style={s.tooltipText}>Total: <strong>{formatBandwidth(tooltipData.total)}</strong></p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      {hasData && (
        <div style={s.legendArea}>
          {mode !== MODE_SPLIT ? (
            <div style={s.legend}>
              <div style={s.legendItem}>
                <div style={{ ...s.legendDot, backgroundColor: '#D7D7CC' }} />
                <span>Download</span>
              </div>
              <div style={s.legendItem}>
                <div style={{ ...s.legendDot, backgroundColor: '#88F4EB' }} />
                <span>Upload</span>
              </div>
            </div>
          ) : (
            // Toggleable WAN chips
            <div style={s.chipsRow}>
              {wanList.map((wan, idx) => {
                const colors = WAN_COLORS[idx % WAN_COLORS.length];
                const hidden = hiddenWans.has(wan.id);
                return (
                  <button
                    key={wan.id}
                    onClick={() => toggleWanVisibility(wan.id)}
                    style={{ ...s.chip, ...(hidden ? s.chipHidden : {}) }}
                    title={hidden ? `Show ${wan.name}` : `Hide ${wan.name}`}
                  >
                    <span style={{ ...s.chipSwatch, background: hidden ? 'rgba(255,255,255,0.2)' : colors.download }} />
                    <span style={{ ...s.chipSwatch, background: hidden ? 'rgba(255,255,255,0.15)' : colors.upload, marginLeft: 2 }} />
                    <span style={{ ...s.chipLabel, ...(hidden ? s.chipLabelHidden : {}) }}>
                      {wan.name}
                    </span>
                    {hidden && <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.5 }}>👁</span>}
                  </button>
                );
              })}
              <span style={s.chipHint}>CLICK CHIP TO HIDE/SHOW</span>
            </div>
          )}
        </div>
      )}

      {/* Stats — TOTAL / SINGLE only */}
      {hasData && mode !== MODE_SPLIT && (
        <div style={s.statsContainer}>
          <div style={s.statsTitle}>BANDWIDTH STATISTICS</div>
          <div className="t247-wan-headrow" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '9px 14px' }}>
            <div>METRIC</div>
            <div style={{ textAlign: 'right' }}>DOWNLOAD</div>
            <div style={{ textAlign: 'right' }}>UPLOAD</div>
            <div style={{ textAlign: 'right' }}>TOTAL</div>
          </div>
          {[
            { label: 'Current', key: 'current' },
            { label: 'Average', key: 'average' },
            { label: 'Peak',    key: 'peak'    },
          ].map(({ label, key }) => (
            <div className="t247-wan-row" key={key} style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr', cursor: 'default', padding: '10px 14px' }}>
              <div className="t247-wan-name" style={{ fontSize: 12 }}>{label}</div>
              <div className="t247-num">{formatBandwidth(stats[key].download)}</div>
              <div className="t247-num">{formatBandwidth(stats[key].upload)}</div>
              <div className="t247-num strong">{formatBandwidth(stats[key].total)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Loading / empty */}
      {isLoading && !hasData && (
        <div style={s.loadingContainer}>
          <div style={s.spinner} />
          <p style={s.loadingText}>Loading bandwidth data...</p>
        </div>
      )}
      {!hasData && !isLoading && (
        <div style={s.noDataContainer}>
          <p style={s.noDataText}>No bandwidth data available</p>
          <p style={s.noDataSubText}>Waiting for data from the device...</p>
        </div>
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  container: {
    maxWidth: 1350,
    fontFamily: "'DM Sans', sans-serif",
  },
  selectorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  selectorLabel: { fontSize: 10, fontWeight: 400, letterSpacing: '0.12em', fontFamily: "'DM Mono', monospace", color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' },
  selectWrapper: { position: 'relative', flex: 1, minWidth: 200 },
  selectButton: {
    width: '100%',
    padding: '9px 14px',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 0,
    backgroundColor: 'var(--color-card)',
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownArrow: { fontSize: 10, color: 'var(--color-label)' },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: 'var(--color-card)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 0,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    zIndex: 1000,
    maxHeight: 300,
    overflowY: 'auto',
  },
  dropdownItem: {
    width: '100%',
    padding: '10px 14px',
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dropdownItemSelected: { backgroundColor: 'rgba(136, 244, 235, 0.14)', color: 'var(--color-success)', fontWeight: 500 },
  checkmark: { color: 'var(--color-success)', fontSize: 13 },
  updateTime: { fontSize: 10.5, fontFamily: "'DM Mono', monospace", color: 'var(--color-label)', textAlign: 'right', marginBottom: 8 },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'visible',
    marginBottom: 8,
    WebkitOverflowScrolling: 'touch',
  },
  legendArea: { marginTop: 10 },
  legend: { display: 'flex', justifyContent: 'center', gap: 24 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontFamily: "'DM Mono', monospace", color: 'var(--color-text-secondary)' },
  legendDot: { width: 9, height: 9, display: 'block' },
  // WAN chips
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 0,
    border: '1px solid rgba(255,255,255,0.16)',
    background: 'var(--color-surface)',
    cursor: 'pointer',
    fontSize: 11,
    fontFamily: "'DM Mono', monospace",
    letterSpacing: '0.02em',
    transition: 'opacity 0.15s',
  },
  chipHidden: { opacity: 0.4, borderStyle: 'dashed' },
  chipSwatch: { display: 'inline-block', width: 7, height: 7 },
  chipLabel: { color: '#FFFFFF', marginLeft: 5 },
  chipLabelHidden: { color: 'var(--color-label)', textDecoration: 'line-through' },
  chipHint: { fontSize: 10, fontFamily: "'DM Mono', monospace", color: 'var(--color-label)', marginLeft: 4 },
  // Tooltip
  tooltip: {
    backgroundColor: 'var(--navy-800, #112023)',
    border: '1px solid rgba(136,244,235,0.3)',
    padding: '9px 13px',
    borderRadius: 0,
    minWidth: 160,
    color: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  },
  tooltipTime: {
    margin: '0 0 6px 0',
    fontSize: 10.5,
    fontFamily: "'DM Mono', monospace",
    fontWeight: 'bold',
    textAlign: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
    paddingBottom: 4,
  },
  tooltipText: {
    margin: '4px 0',
    fontSize: 11,
    fontFamily: "'DM Mono', monospace",
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // Stats
  statsContainer: { marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.1)' },
  statsTitle: { fontSize: 10, fontWeight: 400, letterSpacing: '0.14em', fontFamily: "'DM Mono', monospace", color: 'rgba(255,255,255,0.7)', marginBottom: 12 },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, minHeight: 300 },
  spinner: { width: 32, height: 32, border: '2px solid rgba(255,255,255,0.15)', borderTop: '2px solid #88F4EB', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { marginTop: 16, fontSize: 12, fontFamily: "'DM Mono', monospace", color: 'var(--color-label)' },
  noDataContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, minHeight: 300 },
  noDataText: { fontSize: 12, fontFamily: "'DM Mono', monospace", color: 'var(--color-label)', marginBottom: 8 },
  noDataSubText: { fontSize: 11, fontFamily: "'DM Mono', monospace", color: 'var(--color-label)' },
  errorText: { fontSize: 12, fontFamily: "'DM Mono', monospace", color: 'var(--color-label)', textAlign: 'center', padding: 20 },
};

// Spinner keyframes
if (typeof document !== 'undefined') {
  const el = document.createElement('style');
  el.textContent = `@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`;
  document.head.appendChild(el);
}

export default RealtimeBandwidthChart;
